import React, { useEffect, useState, useContext, useMemo } from 'react';
import { PostHogProvider } from 'posthog-js/react';
import './App.scss';
import { useHistory } from 'react-router-dom';
import { AccessProvider } from '@4r/module-common-authorization';
import { GeolocationProvider } from '@4r/module-common-geolocation';
import { addService, getService, init, MFProvider } from '@4r/mf-host';
import ReactDOM from 'react-dom';
import * as antd from 'antd';
import { FeatureFlagContextValue, FeatureFlagProviderV2 } from '@4r/module-common-feature-flags-mf-host';
import { ConfigContextProvider, useConfig } from '@4r/module-common-mf-app';
import { FaroErrorBoundary } from '@grafana/faro-react';
import { UserContext, UserProvider } from '../contexts/UserContext';
import { Settings } from '../contexts/types';
import { AuthProvider, AuthContext } from '../authentication/AuthProvider';
import AppContent from './AppContent';
import { ReferenceDataProvider } from '../contexts/ReferenceDataContext';
import GeoCoordinatesLogger from './GeoCoordinatesLogger';
import { ContractsProvider } from '../contexts/ContractsContext';
import ImagePreloader from './ImagePreloader';
import HubConfigurationProvider from './HubConfigurationProvider';
import { NotificationProvider } from '../contexts/NotificationContext';
import SignalRUpdatesProvider from '../contexts/SignalRUpdatesProvider';
import getValuesFromToken from '../authorization/getValuesFromToken';
import initAnalytics from '../analytics';
import loadSettings from '../common/loadSettings';
import { attachRouterInstrumentation } from '../observability/faro';
import CurrentOrderIdService from '../services/CurrentOrderIdService';
import { reportOfflineQueueEvent } from '../observability/offlineQueueTelemetry';
import { announceHostSyncIndicator } from '../services/offlineSyncIndicator';

function FeatureFlagAdapter({ children }: React.PropsWithChildren<object>) {
	const settings = useConfig();
	const user = useContext(UserContext);
	const auth = useContext(AuthContext);
	const userId = useMemo(() => user.current?.id || null, [user]);
	// null = context not resolved yet (distinct from [] = resolved anonymous).
	const [contextValues, setContextValues] = useState<FeatureFlagContextValue[] | null>(null);
	useEffect(() => {
		if (userId) {
			getValuesFromToken(userId).then(setContextValues);
		}
	}, [userId, setContextValues]);

	// A user is expected when there is a token in storage. Until that user's flag context is resolved,
	// tell the provider to wait (show a loader) so LD never boots with the anonymous context and then
	// flips after identify(). When nobody is logged in, render immediately as anonymous — no loader.
	// NB: gate on token presence only, NOT isAuthenticationInProgress — the OIDC redirect callback
	// (AuthRoutes → OIDCRedirectCallbackView) renders below this adapter, so gating mid-login would
	// block the callback and break sign-in.
	const userExpected = !!auth.authService?.getAccessToken();
	const isResolvingUser = userExpected && contextValues === null;

	return (
		<FeatureFlagProviderV2
			clientSideId={settings.LAUNCH_DARKLY_CLIENT_ID}
			featureFlagContextUserKey="userId"
			identityTokenUserContextValues={contextValues ?? []}
			isResolvingUser={isResolvingUser}
			loadingFallback={
				<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
					<antd.Spin size="large" />
				</div>
			}
		>
			{children}
		</FeatureFlagProviderV2>
	);
}

const App: React.FunctionComponent = () => {
	const [settings, setSettings] = useState<Settings>();
	const history = useHistory();

	function log(msg: string, ...args: any[]) {
		// eslint-disable-next-line no-console
		console.info(`App: ${msg}`, ...args);
	}

	// Faro itself is initialised in index.tsx before the first render; only the router
	// instrumentation has to wait until a history instance exists.
	useEffect(() => {
		attachRouterInstrumentation(history);
	}, [history]);

	// load settings — resolved from the memoised loader that index.tsx already awaited to
	// initialise Faro, so this does not issue a second request for config.json.
	useEffect(() => {
		loadSettings()
			.then((s) => {
				log('Settings loaded', s);

				// Load Google API with key
				if (window && document) {
					const script = document.createElement('script');
					const body = document.getElementsByTagName('body')[0];
					script.src = `https://maps.googleapis.com/maps/api/js?key=${s.GOOGLE_MAP_KEY}&libraries=places,drawing,geometry`;
					body.appendChild(script);
				}

				init(
					// filter out v1 MFs
					s.APPS.filter((x) => x.ISV1 === 'true').map((x) => ({
						config: x.CONFIG,
						css: x.CSS,
						js: x.JS,
						externalStyles: x.EXTERNAL_STYLES,
						enabled: x.ENABLED === 'true',
					})),
					history,
					[
						{ name: 'React', value: React },
						{ name: 'ReactDOM', value: ReactDOM },
						{ name: 'antd', value: antd },
					],
				);

				// Legacy offline removal: `CurrentOrderIdService` used to be registered
				// by the (now-deleted) `ServiceLocator` inside the legacy OfflineProvider.
				// MFs still read it via `getService('CurrentOrderIdService')`, so register
				// it standalone here (0-dependency, no offline coupling).
				if (!getService('CurrentOrderIdService')) {
					addService('CurrentOrderIdService', new CurrentOrderIdService());
				}

				// The offline command queue moved into `@4r/common-module-offline-services`,
				// mounted by app-mf-work-order — a separate bundle, so it cannot reach our
				// Faro instance directly. It forwards raw queue events here instead and the
				// mapping to our telemetry vocabulary happens host-side.
				if (!getService('OfflineTelemetryService')) {
					addService('OfflineTelemetryService', { report: reportOfflineQueueEvent });
				}

				announceHostSyncIndicator();

				setSettings(s);
			})
			.catch((e) => log('Settings could not be loaded', e));
	}, []);

	// PostHog must be initialised once, not on every render — the previous call sat directly in
	// the render body, so each render re-ran posthog.init and opened another IndexedDB handle
	// for the offline event queue.
	const analytics = useMemo(
		() =>
			settings
				? initAnalytics({
						PUBLIC_POSTHOG_KEY: settings.PUBLIC_POSTHOG_KEY,
						PUBLIC_POSTHOG_HOST: settings.PUBLIC_POSTHOG_HOST,
				  })
				: null,
		[settings],
	);

	if (!settings || !analytics) return null;

	return (
		<>
			<PostHogProvider client={analytics.posthog}>
				<FaroErrorBoundary>
					<ConfigContextProvider outerSettings={settings} config={{}}>
						<MFProvider>
							<AuthProvider settings={settings}>
								<AccessProvider>
									<UserProvider>
										<FeatureFlagAdapter>
											<HubConfigurationProvider>
												<ReferenceDataProvider>
													<NotificationProvider>
														<SignalRUpdatesProvider>
															<ContractsProvider>
																<GeolocationProvider>
																	<GeoCoordinatesLogger>
																		<ImagePreloader>
																			<AppContent />
																		</ImagePreloader>
																	</GeoCoordinatesLogger>
																</GeolocationProvider>
															</ContractsProvider>
														</SignalRUpdatesProvider>
													</NotificationProvider>
												</ReferenceDataProvider>
											</HubConfigurationProvider>
										</FeatureFlagAdapter>
									</UserProvider>
								</AccessProvider>
							</AuthProvider>
						</MFProvider>
					</ConfigContextProvider>
				</FaroErrorBoundary>
			</PostHogProvider>
		</>
	);
};

export default App;
